// Browser-side geometry of the Advanced display (DynamicSwiper):
//   useViewportBreakout  "Full width" = the whole viewport (or the Studio
//                        preview's simulated screen), measured, so it never
//                        causes a horizontal scrollbar (100vw would: it
//                        includes the vertical scrollbar)
//   useDisplayMetrics    real px of margin / padding / border, whatever the
//                        unit, for the optional diagnostic overlay
//   DiagnosticMargin / DiagnosticPadding  the overlay itself (Studio only)
import React, { useLayoutEffect, useState } from "react";

const SIDES = ["top", "right", "bottom", "left"];
const MIN_VIEWPORT = 120;

const px = (value) => Math.round(parseFloat(value) || 0);

// enabled: the box is in "full" width mode. Returns null (no breakout: the
// swiper simply fills its parent) until the geometry is known, outside a
// browser, or in a viewport too small to be real (tests, hidden tabs).
export const useViewportBreakout = (outerRef, enabled) => {
  const [breakout, setBreakout] = useState(null);
  useLayoutEffect(() => {
    if (!enabled) {
      setBreakout(null);
      return undefined;
    }
    const element = outerRef.current;
    if (!element || typeof window === "undefined") return undefined;
    const measure = () => {
      const parent = element.parentElement;
      if (!parent) return;
      // The Studio preview lays a page out at the device width and scales it
      // down: inside it, "the viewport" is that simulated page.
      const viewport = element.closest?.("[data-dsw-viewport]");
      let viewportWidth;
      let parentLeft;
      const parentRect = parent.getBoundingClientRect();
      if (viewport) {
        const viewportRect = viewport.getBoundingClientRect();
        const scale = viewport.offsetWidth ? viewportRect.width / viewport.offsetWidth : 1;
        viewportWidth = viewport.clientWidth;
        parentLeft = (parentRect.left - viewportRect.left) / (scale || 1) - (viewport.clientLeft || 0);
      } else {
        viewportWidth = document.documentElement.clientWidth;
        parentLeft = parentRect.left;
      }
      if (!viewportWidth || viewportWidth < MIN_VIEWPORT) {
        setBreakout(null);
        return;
      }
      const style = window.getComputedStyle(parent);
      const contentLeft = parentLeft + px(style.borderLeftWidth) + px(style.paddingLeft);
      const next = { width: Math.round(viewportWidth), marginLeft: -Math.round(contentLeft) };
      setBreakout((prev) => (prev && prev.width === next.width && prev.marginLeft === next.marginLeft ? prev : next));
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      if (element.parentElement) observer.observe(element.parentElement);
      observer.observe(document.documentElement);
    }
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
      observer?.disconnect();
    };
  }, [outerRef, enabled]);
  return enabled ? breakout : null;
};

export const breakoutStyle = (breakout) => (breakout
  ? { width: `${breakout.width}px`, maxWidth: "none", marginLeft: `${breakout.marginLeft}px`, marginRight: 0 }
  : null);

// { margin, padding, border } in real px (diagnostic overlay only).
export const useDisplayMetrics = (outerRef, boxRef, enabled, deps = []) => {
  const [metrics, setMetrics] = useState(null);
  useLayoutEffect(() => {
    const outer = outerRef.current;
    const box = boxRef.current;
    if (!enabled || !outer || !box || typeof window === "undefined") {
      setMetrics(null);
      return undefined;
    }
    const read = () => {
      const o = window.getComputedStyle(outer);
      const b = window.getComputedStyle(box);
      const side = (style, prefix, suffix = "") => Object.fromEntries(SIDES.map((name) => [
        name, px(style[`${prefix}${name[0].toUpperCase()}${name.slice(1)}${suffix}`]),
      ]));
      const next = { margin: side(o, "margin"), padding: side(b, "padding"), border: side(b, "border", "Width") };
      setMetrics((prev) => (prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    };
    read();
    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(read);
      observer.observe(outer);
      observer.observe(box);
    }
    window.addEventListener("resize", read);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", read);
    };
    // deps: the display box itself (a style change does not resize anything).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outerRef, boxRef, enabled, ...deps]);
  return metrics;
};

const marginBar = (side, margin) => {
  const { top, right, bottom, left } = margin;
  switch (side) {
    case "top": return { top: -top, left: -left, right: -right, height: top };
    case "bottom": return { bottom: -bottom, left: -left, right: -right, height: bottom };
    case "left": return { top: 0, bottom: 0, left: -left, width: left };
    default: return { top: 0, bottom: 0, right: -right, width: right };
  }
};

const paddingBar = (side, padding) => {
  const { top, right, bottom, left } = padding;
  switch (side) {
    case "top": return { top: 0, left: 0, right: 0, height: top };
    case "bottom": return { bottom: 0, left: 0, right: 0, height: bottom };
    case "left": return { top, bottom, left: 0, width: left };
    default: return { top, bottom, right: 0, width: right };
  }
};

// Orange = margin (outside the swiper), green = padding (inside its box);
// the dashed outlines are the box and the slides' frame (CSS).
export const DiagnosticMargin = ({ metrics, label }) => (metrics ? (
  <span className="dsw-diag-layer" aria-hidden="true">
    {SIDES.filter((side) => metrics.margin[side] > 0).map((side) => (
      <span key={side} className={`dsw-diag-bar dsw-diag-margin dsw-diag-${side}`} style={marginBar(side, metrics.margin)}>
        {metrics.margin[side] >= 14 && <em>{label} {metrics.margin[side]}</em>}
      </span>
    ))}
  </span>
) : null);

export const DiagnosticPadding = ({ metrics, label }) => (metrics ? (
  <span className="dsw-diag-layer" aria-hidden="true">
    {SIDES.filter((side) => metrics.padding[side] > 0).map((side) => (
      <span key={side} className={`dsw-diag-bar dsw-diag-padding dsw-diag-${side}`} style={paddingBar(side, metrics.padding)}>
        {metrics.padding[side] >= 14 && <em>{label} {metrics.padding[side]}</em>}
      </span>
    ))}
  </span>
) : null);
